import * as React from 'react'
import { Link, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom'
import { ChevronLeft, Plus, LayoutGrid, Boxes, Sofa, Store, Layers, Clock, ListOrdered, Settings, Trash2, Archive, Image as ImageIcon, Link2, Megaphone, MessageSquare, Waypoints, ChartColumn, UserCog, Tag, FileSignature, CalendarClock, SlidersHorizontal, Banknote, Signpost, BookHeart, Globe } from 'lucide-react'
import { useVenue, useUpdateVenue, useCreateSpace, useDeleteSpace, useSetVenueCover, useUpdateSpace } from '@/hooks/useVenues'
import { Badge } from '@/components/ui/badge'
import { Tooltip } from '@/components/ui/tooltip'
import { useAuth } from '@/auth/AuthContext'
import { atCompanyLevel, can, setsUpIntegrations, venuesUpTarget } from '@/auth/access'
import { ApiError, requestErrorMessage } from '@/helpers/apiClient'
import { Button } from '@/components/ui/button'
import { LoginToVenueButton } from '@/components/LoginToVenueButton'
import { VenueOrganizationSettings } from '@/components/VenueOrganizationSettings'
import { VenueShareLinkSection } from '@/components/VenueShareLinkSection'
import { VenuePublicPageSection } from '@/components/VenuePublicPageSection'
import { VenueReviewLinksSection } from '@/components/VenueReviewLinksSection'
import { VenueTracks } from '@/components/VenueTracks'
import { VenueMarketing } from '@/components/VenueMarketing'
import { VenueGoHighLevelSection } from '@/components/gohighlevel/VenueGoHighLevelSection'
import { VenueSourcesSection } from '@/components/sources/VenueSourcesSection'
import { VenueHoneyBookSection } from '@/components/honeybook/VenueHoneyBookSection'
import { useVenueHoneybook } from '@/hooks/useHoneyBook'
import { VenueLeadStatsSection } from '@/components/lead-stats/VenueLeadStatsSection'
import { VenuePriceListSection } from '@/components/sales/VenuePriceListSection'
import { ContractTemplatesSection } from '@/components/sales/ContractTemplatesEditor'
import { PaymentSchedulesSection } from '@/components/sales/PaymentSchedulesEditor'
import { SalesSettingsSection } from '@/components/sales/SalesSettingsEditor'
import { VenueBusinessSection } from '@/components/sales/VenueBusinessSection'
import { useOrganization } from '@/hooks/useOrganization'
import { useGhlAvailability } from '@/hooks/useGoHighLevel'
import { VenueSchedule } from '@/components/VenueSchedule'
import { CoverPhotoField } from '@/components/CoverPhotoField'
import { RecordHeader } from '@/components/RecordHeader'
import { TableThumbCell, TableThumbHead } from '@/components/TableThumbCell'
import { RowActions, type RowAction } from '@/components/ui/row-actions'
import {
  Table,
  TableHeader,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableActionsHead,
  TableActionsCell,
  TableEmptyRow,
} from '@/components/ui/table'
import { Input } from '@/components/ui/input'
import { Field } from '@/components/ui/field'
import { SectionNav, type PageSection } from '@/components/ui/section-nav'
import { PageContainer } from '@/components/ui/page-container'
import { DetailSkeleton, EmptyState, ErrorState } from '@/components/ui/states'
import { InlineText } from '@/components/ui/inline-text'
import { FormError } from '@/components/ui/form-error'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { VenueFurnitureBody } from '@/components/VenueFurnitureBody'
import { VenueTimelinesPanel } from '@/components/VenueTimelinesPanel'
import { VenueInventoryEditor } from '@/components/VenueInventoryEditor'
import { VenueVendorApprovals } from '@/components/VenueVendorApprovals'
import { VenueStaff } from '@/components/VenueStaff'
import { pixelsToFeet, feetToPixels } from '@lib/constants/scale'
import type { VenueDto } from '@lib/dto/venue.dto'
import { venueFacts } from '@/lib/venueFacts'

/** Round a pixel dimension to whole feet for display. */
function feetLabel(pixels: number): number {
  return Math.round(pixelsToFeet(pixels))
}


/**
 * The venue's name and address, edited in place (Notion direct-manipulation): the
 * title looks like a heading until you focus it, and both save on blur/Enter.
 */
function VenueIdentity({ venue, canEdit }: { venue: VenueDto; /** Renaming it and changing its address. */ canEdit: boolean }) {
  const [name, setName] = React.useState(venue.name)
  const [address, setAddress] = React.useState(venue.address ?? '')
  const update = useUpdateVenue()

  React.useEffect(() => setName(venue.name), [venue.name])
  React.useEffect(() => setAddress(venue.address ?? ''), [venue.address])

  function saveName(): void {
    const trimmed = name.trim()
    if (!trimmed) {
      setName(venue.name)
      return
    }
    if (trimmed !== venue.name) update.mutate({ id: venue.id, input: { name: trimmed } })
  }

  function saveAddress(): void {
    const next = address.trim()
    if (next !== (venue.address ?? '')) {
      update.mutate({ id: venue.id, input: { address: next === '' ? null : next } })
    }
  }

  function blurOnEnter(e: React.KeyboardEvent<HTMLInputElement>): void {
    if (e.key === 'Enter') e.currentTarget.blur()
  }

  if (!canEdit) {
    return (
      <div>
        <p className="text-lg font-semibold">{venue.name}</p>
        <p className="mt-1 text-sm text-ink-muted">{venue.address ?? 'No address yet'}</p>
      </div>
    )
  }

  return (
    <div>
      <InlineText
        value={name}
        onChange={(e) => setName(e.target.value)}
        onBlur={saveName}
        onKeyDown={blurOnEnter}
        aria-label="Venue name"
      />
      <InlineText
        variant="subtle"
        className="mt-1"
        value={address}
        onChange={(e) => setAddress(e.target.value)}
        onBlur={saveAddress}
        onKeyDown={blurOnEnter}
        placeholder="Add an address"
        aria-label="Venue address"
      />
      {update.isError ? <FormError className="mt-2">{requestErrorMessage(update.error)}</FormError> : null}
    </div>
  )
}

/** New-space form: name + starting dimensions (feet, stored in editor pixels). */
function SpaceCreateForm({ venueId, onDone }: { venueId: string; onDone: () => void }) {
  const [name, setName] = React.useState('')
  const [widthFt, setWidthFt] = React.useState(40)
  const [heightFt, setHeightFt] = React.useState(30)
  const create = useCreateSpace()

  function save(): void {
    const trimmed = name.trim()
    if (!trimmed || widthFt <= 0 || heightFt <= 0) return
    create.mutate(
      { venueId, input: { name: trimmed, width: feetToPixels(widthFt), height: feetToPixels(heightFt) } },
      { onSuccess: onDone },
    )
  }

  return (
    <>
      <div className="space-y-5">
        <Field label="Name" htmlFor="new-space-name">
          <Input id="new-space-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Ballroom" autoFocus />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Width (ft)" htmlFor="new-space-width">
            <Input
              id="new-space-width"
              type="number"
              min={1}
              value={widthFt}
              onChange={(e) => setWidthFt(Math.max(1, Number(e.target.value)))}
            />
          </Field>
          <Field label="Height (ft)" htmlFor="new-space-height">
            <Input
              id="new-space-height"
              type="number"
              min={1}
              value={heightFt}
              onChange={(e) => setHeightFt(Math.max(1, Number(e.target.value)))}
            />
          </Field>
        </div>
        {create.isError ? <FormError>{requestErrorMessage(create.error)}</FormError> : null}
      </div>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onDone}>
          Cancel
        </Button>
        <Button pending={create.isPending} type="button" onClick={save} disabled={name.trim() === ''}>
          {create.isPending ? 'Adding…' : 'Add space'}
        </Button>
      </DialogFooter>
    </>
  )
}

/**
 * One space: its first photograph, its name, its size, its actions.
 *
 * The picture is the point of the row. With four similar rooms it is faster than
 * reading four names, and a room that still needs one is visible at a glance —
 * which is why the column is always there and a room without a photograph shows
 * the default rather than a gap.
 */
function SpaceRow({
  venue,
  space,
  canManage,
  onDelete,
  onSetRetired,
}: {
  venue: VenueDto
  space: VenueDto['spaces'][number]
  canManage: boolean
  onDelete: () => void
  /** Take the room out of use, or put it back. */
  onSetRetired: (retired: boolean) => void
}) {
  const navigate = useNavigate()
  const photo = space.photos[0]
  const settingsPath = `/venues/${venue.id}/spaces/${space.id}/settings`

  const actions: RowAction[] = [
    // The drawing tools have no read-only mode, so the room's drawing is for
    // those who can change it; anyone else opens its settings.
    ...(canManage
      ? [
          {
            label: 'Open floor plan',
            icon: LayoutGrid,
            priority: true,
            onSelect: () => void navigate(`/venues/${venue.id}/spaces/${space.id}`),
          } satisfies RowAction,
        ]
      : []),
    {
      label: 'Space settings',
      icon: Settings,
      onSelect: () => void navigate(settingsPath),
    },
    ...(canManage
      ? [
          {
            label: space.retiredAt ? 'Restore space' : 'Retire space',
            icon: Archive,
            onSelect: () => onSetRetired(space.retiredAt === null),
          } satisfies RowAction,
          {
            label: 'Delete space',
            icon: Trash2,
            destructive: true,
            onSelect: onDelete,
          } satisfies RowAction,
        ]
      : []),
  ]

  return (
    <TableRow>
      <TableThumbCell src={photo?.url ?? null} focalPoint={photo?.focalPoint ?? null} />
      <TableCell className="font-medium">
        <Link to={settingsPath} className="transition-colors hover:text-primary">
          {space.name}
        </Link>
        {space.retiredAt ? (
          <Tooltip content="Not offered to new events. Its plans are kept.">
            <Badge className="ml-2">Retired</Badge>
          </Tooltip>
        ) : null}
      </TableCell>
      <TableCell className="tabular-nums text-ink-muted">
        {feetLabel(space.width)} × {feetLabel(space.height)} ft
      </TableCell>
      <TableActionsCell>
        <RowActions label={space.name} actions={actions} />
      </TableActionsCell>
    </TableRow>
  )
}

/** The venue's spaces as a table: a photograph, a name, a size, and its actions. */
function SpacesBody({ venue }: { venue: VenueDto }) {
  const { user } = useAuth()
  const canManage = can(user, 'spaces:edit')
  const [adding, setAdding] = React.useState(false)
  const [deleteTarget, setDeleteTarget] = React.useState<{ id: string; name: string } | null>(null)
  const [deleteError, setDeleteError] = React.useState<string | null>(null)
  /** The delete was refused because events have plans in the room. */
  const [deleteInUse, setDeleteInUse] = React.useState(false)
  const del = useDeleteSpace()
  const update = useUpdateSpace()

  function setRetired(spaceId: string, retired: boolean, after?: () => void): void {
    update.mutate({ venueId: venue.id, spaceId, input: { retired } }, { onSuccess: () => after?.() })
  }

  function confirmDelete(): void {
    if (!deleteTarget) return
    setDeleteError(null)
    setDeleteInUse(false)
    del.mutate(
      { venueId: venue.id, spaceId: deleteTarget.id },
      {
        onSuccess: () => setDeleteTarget(null),
        onError: (e) => {
          setDeleteError(requestErrorMessage(e))
          setDeleteInUse(e instanceof ApiError && e.status === 409)
        },
      },
    )
  }

  return (
    <div>
      <Table
        heading={`${String(venue.spaces.length)} space${venue.spaces.length === 1 ? '' : 's'}`}
        actions={
          canManage ? (
            <Button variant="ghost" size="sm" onClick={() => setAdding(true)}>
              <Plus className="h-4 w-4" /> Add space
            </Button>
          ) : undefined
        }
      >
        <TableHeader>
          <TableThumbHead />
          <TableHead>Space</TableHead>
          <TableHead>Size</TableHead>
          <TableActionsHead />
        </TableHeader>
        <TableBody>
          {venue.spaces.length === 0 ? (
            <TableEmptyRow colSpan={4}>No spaces yet.</TableEmptyRow>
          ) : null}
          {venue.spaces.map((space) => (
            <SpaceRow
              key={space.id}
              venue={venue}
              space={space}
              canManage={canManage}
              onDelete={() => {
                setDeleteError(null)
                setDeleteInUse(false)
                setDeleteTarget({ id: space.id, name: space.name })
              }}
              onSetRetired={(retired) => setRetired(space.id, retired)}
            />
          ))}
        </TableBody>
      </Table>
      <Dialog open={adding} onOpenChange={setAdding}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>New space</DialogTitle>
            <DialogDescription>Add a space to {venue.name}. Draw its background and set inventory afterward.</DialogDescription>
          </DialogHeader>
          {adding ? <SpaceCreateForm venueId={venue.id} onDone={() => setAdding(false)} /> : null}
        </DialogContent>
      </Dialog>
      <Dialog
        open={deleteTarget !== null}
        onOpenChange={(open) => {
          if (!open) {
            setDeleteTarget(null)
            setDeleteError(null)
          }
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete space?</DialogTitle>
            <DialogDescription>
              {deleteTarget
                ? `Remove “${deleteTarget.name}” from ${venue.name}, with its saved layouts, its photographs and any stock locked to it. This cannot be undone. A space with event plans can’t be deleted — retire it instead to stop offering it to new events and keep its plans.`
                : null}
            </DialogDescription>
          </DialogHeader>
          {deleteError ? <FormError>{deleteError}</FormError> : null}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setDeleteTarget(null)
                setDeleteError(null)
              }}
            >
              Cancel
            </Button>
            {deleteInUse && deleteTarget ? (
              <Button
                type="button"
                variant="outline"
                disabled={update.isPending}
                onClick={() => setRetired(deleteTarget.id, true, () => setDeleteTarget(null))}
              >
                <Archive className="h-4 w-4" /> Retire instead
              </Button>
            ) : null}
            <Button pending={del.isPending} type="button" variant="destructive" onClick={confirmDelete} disabled={!deleteTarget}>
              <Trash2 className="h-4 w-4" />
              {del.isPending ? 'Deleting…' : 'Delete space'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

/**
 * Anchors from the old combined settings page. `tracks` is a section in its own
 * right now, so it needs no translation; `danger` lives inside Settings, which
 * only exists at organization level. `managers` is the Staff section now.
 */
const ORGANIZATION_SETTINGS_HASHES = new Set(['danger'])

/**
 * A venue: identity (name + address, in place), spaces, inventory, vendor
 * approvals and tracks — the whole of the venue, which is why these are sections
 * here rather than pages to leave for.
 *
 * One further section, Settings, appears only at organization level and holds
 * what the organization decides rather than the venue: its managers, and whether
 * it stays.
 */
/**
 * A venue's cover photograph. Deliberately its own section rather than part of
 * the identity header: it is the one venue-level field its own staff own, so it
 * lives where they can find it, next to the rest of the venue's own settings.
 */
function VenuePhotoBody({ venue, canEdit }: { venue: VenueDto; /** Changing it and its focal point. */ canEdit: boolean }) {
  const setCover = useSetVenueCover()
  return (
    <CoverPhotoField
      disabled={!canEdit}
      coverImageUrl={venue.coverImageUrl}
      focalPoint={venue.coverFocalPoint}
      // The hero this cover appears in crops at 50% 45%, so the picker's preview
      // and its reset target have to agree with the hero rather than with center.
      unfocusedPosition="50% 45%"
      kind="venue"
      onSave={async (coverImageUrl) => {
        // The point goes with the photograph it was chosen on. Cleared in the
        // same write, so a replacement cannot inherit the last one's crop and
        // open framed on a corner of itself for no reason anybody can see.
        await setCover.mutateAsync({ id: venue.id, coverImageUrl, coverFocalPoint: null })
      }}
      onFocalPoint={(coverFocalPoint) => {
        // The point only. `coverImageUrl` is what the hero is *displaying* — a
        // signed URL that expires within the hour — and sending it back was
        // storing it in place of the key, which is how a venue's photograph
        // stopped loading and could not be recovered by reloading.
        void setCover.mutateAsync({ id: venue.id, coverFocalPoint })
      }}
    />
  )
}

export function VenueDetailPage() {
  const { user } = useAuth()
  const upTarget = venuesUpTarget(user)
  const { venueId } = useParams()
  const location = useLocation()
  const id = venueId ?? ''
  const hashId = decodeURIComponent(location.hash.replace(/^#/, ''))
  const atOrganizationLevel = atCompanyLevel(user)
  const venueQuery = useVenue(id)
  // Marketing is sold separately. Without it the section is not shown at all —
  // not grayed, not an upsell — because the feature says so (`offMode: 'hidden'`).
  // The page waits for the answer rather than guessing: drawn without Marketing
  // first, a `#marketing` link lands on Spaces and then jumps; drawn with it, a
  // company that lacks it sees the section appear and vanish. If the answer
  // never comes, the rest of the page still does — one optional section is no
  // reason to take away Spaces, Schedule and everything else.
  const organizationQuery = useOrganization()
  // Whether this venue has GoHighLevel connected, waited for the same way: a
  // company turns GoHighLevel on for the venues that use it, and its lead stats
  // pane belongs only to those.
  const companyHasGoHighLevel = organizationQuery.data?.features.includes('gohighlevel') ?? false
  const companyHasHoneyBook = organizationQuery.data?.features.includes('honeybook') ?? false
  const ghlAvailability = useGhlAvailability(id, companyHasGoHighLevel)
  // Whether this venue has HoneyBook connected: only asked of whoever sets up
  // integrations, who are the ones its HoneyBook settings are for.
  const honeybookQuery = useVenueHoneybook(id, companyHasHoneyBook && setsUpIntegrations(user))

  // Keep old deep links landing somewhere deliberate. SectionNav picks the first
  // section when it does not recognize the hash, so a link left to itself would
  // quietly open Spaces — which is why each of these is translated rather than
  // allowed to fall through: to Settings where that section exists, and to Tracks
  // inside a venue, where it never will.
  if (id !== '' && hashId === 'managers') {
    return <Navigate to={`/venues/${id}/settings#staff`} replace />
  }
  if (id !== '' && (ORGANIZATION_SETTINGS_HASHES.has(hashId) || hashId === 'settings')) {
    const target = atOrganizationLevel ? 'settings' : 'tracks'
    if (hashId !== target) {
      return <Navigate to={`/venues/${id}/settings#${target}`} replace />
    }
  }

  if (venueQuery.isPending || organizationQuery.isLoading || ghlAvailability.isLoading) {
    return (
      <PageContainer width="wide">
        <DetailSkeleton />
      </PageContainer>
    )
  }
  if (venueQuery.isError) {
    return (
      <PageContainer width="wide" className="space-y-4">
        <ErrorState message={requestErrorMessage(venueQuery.error)} onRetry={() => void venueQuery.refetch()} />
        <div className="flex justify-center">
          <Button variant="ghost" size="sm" asChild>
            <Link to={upTarget.to}>Back to {upTarget.label.toLowerCase()}</Link>
          </Button>
        </div>
      </PageContainer>
    )
  }

  const venue = venueQuery.data
  const hasMarketing = organizationQuery.data?.features.includes('marketing') ?? false
  const venueHasGoHighLevel = companyHasGoHighLevel && ghlAvailability.data?.connected === true
  const venueHasHoneyBook = companyHasHoneyBook && (honeybookQuery.data?.connection ?? null) !== null
  // Each section is for those whose role sees its area, and changes it only with
  // Edit (docs/custom-roles.spec.md, "Permissions").
  const seen = (shown: boolean, section: PageSection): PageSection[] => (shown ? [section] : [])
  const sections: PageSection[] = [
    ...seen(can(user, 'spaces:view'), {
      id: 'spaces',
      label: 'Spaces',
      icon: LayoutGrid,
      description: "The rooms and areas events are planned in. Draw each one's background from its editor.",
      content: <SpacesBody venue={venue} />,
    }),
    ...seen(can(user, 'venueSetup:view'), {
      id: 'photo',
      label: 'Photo',
      icon: ImageIcon,
      description:
        "The photograph that represents this venue wherever it is listed. Its staff can change it; the venue's name and address belong to the organization.",
      content: <VenuePhotoBody venue={venue} canEdit={can(user, 'venueSetup:edit')} />,
    }),
    ...seen(can(user, 'venueSetup:view'), {
      id: 'public-page',
      label: 'Public page',
      icon: Globe,
      description: 'The venue’s own page on csvenueview.com: off until it’s turned on, and only shown while it has a photo, an address and a description.',
      content: (
        <VenuePublicPageSection
          key={venue.id}
          venueId={venue.id}
          venueName={venue.name}
          canEdit={can(user, 'venueSetup:edit')}
        />
      ),
    }),
    ...seen(can(user, 'venueSetup:view'), {
      id: 'review-links',
      label: 'Review links',
      icon: MessageSquare,
      description: 'Where a couple can review this venue in public: Google, Facebook, Yelp, The Knot or any other site. Every one is offered, whatever they tell you.',
      content: <VenueReviewLinksSection key={venue.id} venueId={venue.id} canEdit={can(user, 'venueSetup:edit')} />,
    }),
    ...seen(can(user, 'furniture:view'), {
      id: 'furniture',
      label: 'Furniture',
      icon: Sofa,
      description:
        'The pieces this venue has, and the arrangements built from them. How many of each it owns is the next section along.',
      content: <VenueFurnitureBody venue={venue} canEdit={can(user, 'furniture:edit')} />,
    }),
    ...seen(can(user, 'furniture:view'), {
      id: 'inventory',
      label: 'Inventory',
      icon: Boxes,
      description: 'How many of each piece this venue owns. Keep an item movable across all spaces, or lock some units to one space.',
      content: <VenueInventoryEditor venue={venue} canEdit={can(user, 'furniture:edit')} />,
    }),
    ...seen(can(user, 'vendors:view'), {
      id: 'vendors',
      label: 'Vendors',
      icon: Store,
      description: "The companies working with this venue: add one, and approve which of its services are available here — e.g. their furniture in this venue's events.",
      content: <VenueVendorApprovals venueId={venue.id} venueName={venue.name} canEdit={can(user, 'vendors:edit')} />,
    }),
    ...seen(can(user, 'people:view'), {
      id: 'staff',
      label: 'Staff',
      icon: UserCog,
      description: 'The people who work at this venue, and their roles. Someone on a company-wide role works at every venue, so isn’t listed.',
      content: <VenueStaff venue={venue} />,
    }),
    ...seen(can(user, 'venueSetup:view'), {
      id: 'schedule',
      label: 'Schedule',
      icon: Clock,
      description:
        "The venue's clock and its usual evening. The clock is what tells the app when an event has finished; the evening is what a new booking starts from.",
      content: <VenueSchedule venue={venue} canEdit={can(user, 'venueSetup:edit')} />,
    }),
    ...seen(can(user, 'venueSetup:view'), {
      id: 'timelines',
      label: 'Timelines',
      icon: ListOrdered,
      description:
        'The shapes this venue\u2019s days usually take. The one marked default is copied onto a new event the moment it has a date; any of them can be applied to an event afterwards.',
      content: <VenueTimelinesPanel venueId={venue.id} canEdit={can(user, 'venueSetup:edit')} />,
    }),
    ...seen(can(user, 'venueSetup:view'), {
      id: 'share-link',
      label: 'Share link',
      icon: Link2,
      description:
        'One link a setup crew can bookmark. It shows the next two weeks of plans and instructions, and never an event\u2019s name, clients or guest count.',
      content: <VenueShareLinkSection venueId={venue.id} canEdit={can(user, 'venueSetup:edit')} />,
    }),
    ...seen(hasMarketing && can(user, 'marketing:view'), {
      id: 'marketing',
      label: 'Marketing',
      icon: Megaphone,
      description:
        'The content the public pricing page for this venue is built from. A venue that runs several tracks has a page for each.',
      content: <VenueMarketing venue={venue} canEdit={can(user, 'marketing:edit')} />,
    }),
    ...seen(can(user, 'spaces:view'), {
      id: 'tracks',
      label: 'Tracks',
      icon: Layers,
      description:
        'How the venue runs events — one at a time, or several concurrently on separate tracks.',
      // Tracks warn that deleting one takes its pricing page, read from Marketing.
      content: (
        <VenueTracks venue={venue} hasMarketing={hasMarketing && can(user, 'marketing:view')} canEdit={can(user, 'spaces:edit')} />
      ),
    }),
  ]
  // Where a venue's GoHighLevel sub-accounts are connected, for whoever sets up
  // the company's integrations, working across it or at their own venues: it is
  // there for every venue, connected or not. Kept out of a venue someone from
  // across the company has entered, where they work as its staff.
  if (setsUpIntegrations(user) && companyHasGoHighLevel) {
    sections.push({
      id: 'gohighlevel',
      label: 'GoHighLevel',
      icon: Waypoints,
      description: 'The GoHighLevel sub-accounts that hold this venue’s leads, so events can be linked to the lead they came from.',
      content: <VenueGoHighLevelSection venue={venue} />,
    })
  }
  // The HoneyBook company the venue runs its clients in (docs/honeybook.spec.md),
  // for whoever sets up integrations, there for every venue, connected or not.
  if (setsUpIntegrations(user) && companyHasHoneyBook) {
    sections.push({
      id: 'honeybook',
      label: 'HoneyBook',
      icon: BookHeart,
      description: 'The HoneyBook company this venue runs its clients in: its pipeline counts here, and its booked projects become events.',
      content: <VenueHoneyBookSection venueId={venue.id} tracks={venue.tracks} />,
    })
  }
  // Where the venue's couples come from (docs/ad-attribution.spec.md): what its
  // lead source names mean and what its listing sites cost, set by whoever sets
  // up integrations. Only where the venue has GoHighLevel or HoneyBook: the names are their leads'.
  if (setsUpIntegrations(user) && (venueHasGoHighLevel || venueHasHoneyBook)) {
    sections.push({
      id: 'sources',
      label: 'Where couples come from',
      icon: Signpost,
      description: 'Which channel each of this venue’s lead sources means, and the listing sites it pays for.',
      content: <VenueSourcesSection venueId={venue.id} />,
    })
  }
  // A venue's own ingredients for the company's lead stats: its staff adjust them,
  // so this pane is in the venue's own settings too (docs/gohighlevel-stats.spec.md).
  // Only for a venue with GoHighLevel connected: there is nothing to adjust without.
  if (venueHasGoHighLevel && can(user, 'leadStats:view')) {
    sections.push({
      id: 'lead-stats',
      label: 'Lead stats',
      icon: ChartColumn,
      description: 'What each of the company’s numbers is made of at this venue, where its GoHighLevel differs.',
      content: <VenueLeadStatsSection venueId={venue.id} canEdit={can(user, 'leadStats:edit')} />,
    })
  }
  // What the venue sells and charges (docs/sales.spec.md, "The price list"). Every
  // company has Sales; changing prices takes the Price list switch.
  if (can(user, 'sales:view')) {
    sections.push({
      id: 'price-list',
      label: 'Price list',
      icon: Tag,
      description: 'Packages by season and day of the week, per-guest prices, add-ons, tax and service charge.',
      content: <VenuePriceListSection venueId={venue.id} />,
    })
    sections.push({
      id: 'contracts',
      label: 'Contracts',
      icon: FileSignature,
      description: 'The venue’s contract templates, filled in from each proposal and signed in the app.',
      content: <ContractTemplatesSection venueId={venue.id} venueName={venue.name} />,
    })
    sections.push({
      id: 'payment-schedules',
      label: 'Payment schedules',
      icon: CalendarClock,
      description: 'The schedules a proposal starts from: a deposit, then the payments and when each is due.',
      content: <PaymentSchedulesSection venueId={venue.id} />,
    })
    sections.push({
      id: 'sales-settings',
      label: 'Sales settings',
      icon: SlidersHorizontal,
      description: 'How long a proposal stays open, a bank-transfer discount, and payment reminders.',
      content: <SalesSettingsSection venueId={venue.id} />,
    })
    sections.push({
      id: 'getting-paid',
      label: 'Getting paid',
      icon: Banknote,
      description: 'Who the venue’s payments go to: the company’s business, or its own.',
      content: <VenueBusinessSection venueId={venue.id} venueName={venue.name} />,
    })
  }
  // The organization's decisions about the venue, kept out of the venue-level
  // panes entirely rather than shown-but-empty: inside a venue there is nothing
  // here to see, and a pane that exists only to say so is worse than no pane.
  if (atOrganizationLevel && (can(user, 'venues:edit') || can(user, 'deleteEvents'))) {
    sections.push({
      id: 'settings',
      label: 'Settings',
      icon: Settings,
      description: 'The venue’s name and address, and whether it stays.',
      content: (
        <div className="space-y-8">
          <section className="space-y-3">
            <VenueIdentity venue={venue} canEdit={can(user, 'venues:edit')} />
          </section>
          <VenueOrganizationSettings venue={venue} />
        </div>
      ),
    })
  }

  return (
    <PageContainer width="wide" className="space-y-6">
      <Link
        to={upTarget.to}
        className="inline-flex items-center gap-1 text-sm font-medium text-ink-muted transition-colors hover:text-ink"
      >
        <ChevronLeft className="h-4 w-4" /> {upTarget.label}
      </Link>

      <RecordHeader
        kind="Venue"
        title={venue.name}
        src={venue.coverImageUrl}
        focalPoint={venue.coverFocalPoint}
        facts={venueFacts(venue)}
        actions={<LoginToVenueButton venueId={venue.id} size="sm" fullLabel />}
      />

      <div>
        {organizationQuery.isError ? (
          <p className="mb-4 text-sm text-ink-muted">
            Couldn’t check which features your organization has, so Marketing, GoHighLevel and HoneyBook are hidden for now.{' '}
            <Button variant="link" size="sm" className="h-auto p-0" onClick={() => void organizationQuery.refetch()}>
              Try again
            </Button>
          </p>
        ) : null}
        {sections.length > 0 ? (
          <SectionNav sections={sections} ariaLabel="Venue sections" />
        ) : (
          <EmptyState icon={Settings} title="Nothing to set up here" description="Your role doesn’t include any of this venue’s settings." />
        )}
      </div>
    </PageContainer>
  )
}
